import { toast } from "react-toastify";

export const SESSION_STORAGE_KEY = `${process.env.REACT_APP_STORAGE_NAMESPACE}_notifications`;

type NotificationType = "info" | "success" | "warning" | "error";
type NotificationPayload = { message: string } | string;

type NotificationHandler = ((payload: NotificationPayload) => void) & {
  show: (payload: NotificationPayload) => void;
  queue: (payload: NotificationPayload) => void;
};

const enqueue = (item: any) => {
  const queue = loadQueueFromSessionStorage();
  queue.push(item);
  saveQueueToSessionStorage(queue);
};

const dequeueAll = () => {
  const queue = loadQueueFromSessionStorage();
  const dequeuedItems = [...queue];
  saveQueueToSessionStorage([]);
  return dequeuedItems;
};

const saveQueueToSessionStorage = (queue: any) => {
  sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(queue));
};

const loadQueueFromSessionStorage = () => {
  const queueData = sessionStorage.getItem(SESSION_STORAGE_KEY);
  return queueData ? JSON.parse(queueData) : [];
};

const normalizeMessage = (payload: NotificationPayload) =>
  typeof payload === "string" ? payload : payload.message;

const notifyNow = (type: NotificationType, payload: NotificationPayload) => {
  const message = normalizeMessage(payload);
  const toastFn = toast[type] ?? toast.error;
  toastFn(message, {
    position: toast.POSITION.TOP_RIGHT,
  });
};

const queueForNextLoad = (type: NotificationType, payload: NotificationPayload) =>
  enqueue({ message: normalizeMessage(payload), type, timestamp: Date.now() });

const createNotifier = (type: NotificationType): NotificationHandler => {
  const show = (payload: NotificationPayload) => notifyNow(type, payload);
  const queue = (payload: NotificationPayload) => queueForNextLoad(type, payload);

  const callable = ((payload: NotificationPayload) => show(payload)) as NotificationHandler;
  callable.show = show;
  callable.queue = queue;

  return callable;
};

export const info = createNotifier("info");
export const success = createNotifier("success");
export const warning = createNotifier("warning");
export const error = createNotifier("error");

const notifiersByType: Record<NotificationType, NotificationHandler> = {
  info,
  success,
  warning,
  error,
};

const onReload = () => {
  const now = Date.now();
  const dequeuedItems = dequeueAll();

  for (const item of dequeuedItems) {
    const { message, type, timestamp } = item;
    const itemAgeInSeconds = (now - timestamp) / 1000;

    if (itemAgeInSeconds <= 30 && notifiersByType[type as NotificationType]) {
      notifiersByType[type as NotificationType].show(message);
    }
  }
};

const notifications = {
  info,
  success,
  warning,
  error,
  onReload,
};

export default notifications;
